/**
 * HTTP RPC Transport 适配器
 * 将旧的 HTTP post 函数桥接到新的 @protobuf-ts RpcTransport 接口
 */
import {
  RpcTransport,
  RpcOptions,
  UnaryCall,
  MethodInfo,
  RpcError,
  RpcMetadata,
  ServerStreamingCall,
  ClientStreamingCall,
  DuplexStreamingCall
} from "@protobuf-ts/runtime-rpc";
import { HttpResponse } from "../../assets/javascripts/http";

/**
 * HTTP RPC Transport 实现
 * 兼容旧的 API 调用方式
 */
export class HttpRpcTransport implements RpcTransport {
  private baseUrl: string;
  private postFn: (url: string, headers: any, body: any) => Promise<HttpResponse>;

  constructor(baseUrl: string, postFn: (url: string, headers: any, body: any) => Promise<HttpResponse>) {
    this.baseUrl = baseUrl;
    this.postFn = postFn;
  }

  mergeOptions(options?: Partial<RpcOptions>): RpcOptions {
    return {
      ...options
    } as RpcOptions;
  }

  unary<I extends object, O extends object>(
    method: MethodInfo<I, O>,
    input: I,
    options: RpcOptions
  ): UnaryCall<I, O> {
    // 构造请求 URL
    const url = `${this.baseUrl}/${method.service.typeName}/${method.name}`;
    
    // 构造请求头
    const headers = {
      "Content-Type": "application/json",
      ...(options.meta || {})
    };

    // 创建 Promise 来处理请求
    const responsePromise = this.postFn(url, headers, input)
      .then((httpResponse: HttpResponse) => {
        // 检查响应状态
        if (httpResponse.status >= 400) {
          const errorMessage = `Request failed with status code ${httpResponse.status}`;
          console.error('RPC Error:', {
            url,
            status: httpResponse.status,
            body: httpResponse.body,
            request: input
          });
          throw new RpcError(
            errorMessage,
            "HTTP_ERROR"
          );
        }

        // 返回响应体
        return httpResponse.body as O;
      })
      .catch((error) => {
        // 转换为 RpcError
        if (error instanceof RpcError) {
          throw error;
        }
        
        console.error('RPC Transport Error:', error);
        throw new RpcError(
          error.message || "Unknown error",
          "UNKNOWN"
        );
      });

    // 创建响应头和尾的 Promise
    const responseHeadersPromise = Promise.resolve({} as RpcMetadata);
    const responseTrailersPromise = Promise.resolve({} as RpcMetadata);
    const statusPromise = responsePromise.then(() => ({
      code: "OK",
      detail: ""
    }));

    // 返回 UnaryCall 对象
    return new UnaryCall<I, O>(
      method,
      options.meta || {},
      input,
      responseHeadersPromise,
      responsePromise,
      statusPromise,
      responseTrailersPromise
    );
  }

  // 实现其他必需的方法（暂不支持流式调用）
  serverStreaming<I extends object, O extends object>(
    method: MethodInfo<I, O>,
    input: I,
    options: RpcOptions
  ): ServerStreamingCall<I, O> {
    throw new Error("Server streaming not supported in this transport");
  }

  clientStreaming<I extends object, O extends object>(
    method: MethodInfo<I, O>,
    options: RpcOptions
  ): ClientStreamingCall<I, O> {
    throw new Error("Client streaming not supported in this transport");
  }

  duplex<I extends object, O extends object>(
    method: MethodInfo<I, O>,
    options: RpcOptions
  ): DuplexStreamingCall<I, O> {
    throw new Error("Duplex streaming not supported in this transport");
  }
}
